import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";

// ===== ANAGRAFICA SEZIONI =====
// Le descrizioni testuali vivono nella tabella `guida_descrizioni` su Supabase
// (caricate dinamicamente da SezioneDrawer / modificabili via BloccoDescrizione).
export const SEZIONI_GUIDA = [
  { id: "dashboard",      label: "Dashboard",            icon: "📊" },
  { id: "monitor-live",   label: "Monitor Live",         icon: "📅" },
  { id: "diario",         label: "Diario Operativo",     icon: "📓" },
  { id: "interventi-db",  label: "Interventi su DB",     icon: "🏥" },
  { id: "creazione",      label: "Creazione Sistemi",    icon: "⚙️" },
  { id: "portfolio",      label: "Portfolio Sistemi",    icon: "💼" },
  { id: "strategie",      label: "Portfolio Strategie",  icon: "📋" },
  { id: "dati",           label: "Esporta Database",     icon: "📥" },
  { id: "statistiche",    label: "Statistiche",          icon: "🔍" },
  { id: "setup-stagione", label: "Setup nuova stagione", icon: "🚀" },
  { id: "impostazioni",   label: "Impostazioni",         icon: "🔧" },
  { id: "mail-sistema",   label: "Mail di sistema",      icon: "📨" },
  { id: "note-rilascio",  label: "Note di rilascio",     icon: "📝" },
  { id: "anagrafica-storico", label: "Anagrafica del Database storico", icon: "📚" },
];

// ===== STILI =====
const S = {
  // Overlay scuro dietro il drawer
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)",
    zIndex: 1100,
  },
  // Drawer laterale
  drawer: {
    position: "fixed", top: 0, right: 0, height: "100vh",
    width: 420, background: "#fff",
    boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    zIndex: 1101, display: "flex", flexDirection: "column",
    fontFamily: "'DM Sans', sans-serif",
  },
  // Header drawer
  drawerHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: "1px solid #E2E8F0",
    background: "#1B2846",
  },
  drawerTitolo: {
    fontSize: 15, fontWeight: 700, color: "#fff",
    display: "flex", alignItems: "center", gap: 8,
  },
  drawerChiudi: {
    background: "none", border: "none", color: "#94A3B8",
    fontSize: 18, cursor: "pointer", padding: "4px 8px",
    borderRadius: 6, lineHeight: 1,
  },
  // Body drawer scrollabile
  drawerBody: {
    flex: 1, overflowY: "auto", padding: "20px",
  },
  // Blocco descrizione fissa
  bloccoFisso: {
    background: "#F8FAFC", border: "1px solid #E2E8F0",
    borderRadius: 10, padding: "16px 18px", marginBottom: 16,
  },
  bloccoFissoLabel: {
    fontSize: 10, fontWeight: 700, color: "#94A3B8",
    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10,
  },
  bloccoFissoTesto: {
    fontSize: 13, color: "#374151", lineHeight: 1.75, whiteSpace: "pre-wrap",
  },
  // Singolo blocco nota
  bloccoNota: {
    background: "#FFFBEB", border: "1px solid #FDE68A",
    borderRadius: 10, padding: "14px 16px", marginBottom: 12,
  },
  bloccoNotaHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 10, gap: 8,
  },
  titoloNota: {
    fontSize: 12, fontWeight: 700, color: "#92400E",
    background: "none", border: "none", outline: "none",
    fontFamily: "'DM Sans', sans-serif", flex: 1,
    cursor: "text", padding: "2px 4px", borderRadius: 4,
  },
  titoloNotaEdit: {
    fontSize: 12, fontWeight: 700, color: "#92400E",
    background: "#fff", border: "1px solid #FCD34D",
    outline: "none", fontFamily: "'DM Sans', sans-serif",
    flex: 1, padding: "2px 6px", borderRadius: 4,
  },
  azioniNota: { display: "flex", gap: 4, flexShrink: 0 },
  btnSm: (variant) => ({
    fontSize: 10, fontWeight: 600, padding: "3px 8px",
    borderRadius: 5, cursor: "pointer", border: "none",
    fontFamily: "'DM Sans', sans-serif",
    background: variant === "primary" ? "#D97706"
               : variant === "danger"  ? "#EF4444"
               : "#FEF3C7",
    color: variant === "primary" ? "#fff"
         : variant === "danger"  ? "#fff"
         : "#92400E",
    border: variant === "ghost" ? "1px solid #FCD34D" : "none",
  }),
  btnAggiungi: {
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 12, fontWeight: 600, color: "#D97706",
    background: "none", border: "2px dashed #FCD34D",
    borderRadius: 8, padding: "10px 16px", cursor: "pointer",
    width: "100%", justifyContent: "center",
    fontFamily: "'DM Sans', sans-serif", marginTop: 4,
  },
};

// ===== RENDERER HTML (visualizzazione note salvate) =====
function NoteRenderer({ html }) {
  if (!html || html.trim() === "") {
    return (
      <div style={{ fontSize: 12, color: "#A16207", fontStyle: "italic" }}>
        Nessuna nota. Clicca "Modifica" per aggiungerne.
      </div>
    );
  }
  return (
    <div
      className="note-render"
      style={{ fontSize: 13, color: "#374151", lineHeight: 1.75 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ===== EDITOR RICH TEXT =====
function RichEditor({ valore, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== valore) {
      editorRef.current.innerHTML = valore || "";
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const eseguiCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || "");
  };

  const inserisciImmagine = () => {
    let url = prompt("Incolla il link dell'immagine (Dropbox, Imgur, ecc.):");
    if (!url || !url.trim()) return;
    url = url.trim();
    // Correzione automatica link Dropbox: dl=0 → raw=1
    url = url.replace(/[?&]dl=0/, (match) => match.replace("dl=0", "raw=1"));
    if (!url.includes("raw=1") && url.includes("dropbox.com")) {
      url += (url.includes("?") ? "&" : "?") + "raw=1";
    }
    const larghezza = prompt("Larghezza immagine in % (es. 50, 75, 100). Lascia vuoto per 100%:");
    const w = larghezza && !isNaN(parseInt(larghezza))
      ? parseInt(larghezza) + "%"
      : "100%";
    eseguiCmd(
      "insertHTML",
      `<img src="${url}" alt="screenshot" style="width:${w};max-width:100%;border-radius:6px;margin:8px 0;display:block;" />`
    );
  };

  const toolbarBtn = (label, onClick, title) => (
    <button
      key={label}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title || label}
      style={{
        background: "#FEF3C7", border: "1px solid #FCD34D",
        borderRadius: 4, padding: "2px 7px", cursor: "pointer",
        fontSize: 11, color: "#92400E", fontFamily: "'DM Sans', sans-serif",
        fontWeight: 600, lineHeight: 1.5,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {toolbarBtn("G", () => eseguiCmd("bold"), "Grassetto")}
        {toolbarBtn("C", () => eseguiCmd("italic"), "Corsivo")}
        {toolbarBtn("S", () => eseguiCmd("underline"), "Sottolineato")}
        <div style={{ width: 1, background: "#FCD34D", margin: "0 2px" }} />
        {toolbarBtn("H1", () => eseguiCmd("formatBlock", "<h3>"), "Titolo")}
        {toolbarBtn("H2", () => eseguiCmd("formatBlock", "<h4>"), "Sottotitolo")}
        {toolbarBtn("¶", () => eseguiCmd("formatBlock", "<p>"), "Paragrafo")}
        <div style={{ width: 1, background: "#FCD34D", margin: "0 2px" }} />
        {toolbarBtn("• Lista", () => eseguiCmd("insertUnorderedList"), "Elenco puntato")}
        {toolbarBtn("1. Lista", () => eseguiCmd("insertOrderedList"), "Elenco numerato")}
        <div style={{ width: 1, background: "#FCD34D", margin: "0 2px" }} />
        {toolbarBtn("🖼 Img", inserisciImmagine, "Inserisci immagine")}
      </div>

      {/* Area contentEditable */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        style={{
          minHeight: 160, padding: "10px 12px",
          border: "1px solid #FCD34D", borderRadius: 8,
          fontSize: 13, color: "#374151", lineHeight: 1.75,
          outline: "none", background: "#fff",
          fontFamily: "'DM Sans', sans-serif",
          overflowY: "auto",
        }}
      />

      {/* Stili globali per editor e renderer */}
      <style>{`
        [contenteditable] h3 { font-size: 14px; font-weight: 700; color: #1E293B; margin: 8px 0 4px 0; }
        [contenteditable] h4 { font-size: 13px; font-weight: 600; color: #374151; margin: 6px 0 2px 0; }
        [contenteditable] ul, [contenteditable] ol { margin: 4px 0; padding-left: 18px; }
        [contenteditable] li { margin: 2px 0; }
        [contenteditable] p { margin: 4px 0; }
        .note-render h3 { font-size: 14px; font-weight: 700; color: #1E293B; margin: 8px 0 4px 0; }
        .note-render h4 { font-size: 13px; font-weight: 600; color: #374151; margin: 6px 0 2px 0; }
        .note-render ul, .note-render ol { margin: 4px 0; padding-left: 18px; }
        .note-render li { margin: 2px 0; }
        .note-render p { margin: 4px 0; }
        .note-render img { max-width: 100%; border-radius: 6px; margin: 8px 0; display: block; }
      `}</style>
    </div>
  );
}

// ===== SINGOLO BLOCCO NOTA =====
function BloccoNota({ nota, onSalva, onElimina }) {
  const [modalita, setModalita] = useState("visualizza"); // "visualizza" | "modifica"
  const [titoloEdit, setTitoloEdit] = useState(nota.titolo || "Nuova nota");
  const [contenutoEdit, setContenutoEdit] = useState(nota.contenuto || "");
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState("");

  // Aggiorna stato locale se la nota cambia dall'esterno
  useEffect(() => {
    setTitoloEdit(nota.titolo || "Nuova nota");
    setContenutoEdit(nota.contenuto || "");
    setModalita("visualizza");
    setErrore("");
  }, [nota.id]);

  const apriModifica = () => {
    setTitoloEdit(nota.titolo || "Nuova nota");
    setContenutoEdit(nota.contenuto || "");
    setModalita("modifica");
    setErrore("");
  };

  const annulla = () => {
    setTitoloEdit(nota.titolo || "Nuova nota");
    setContenutoEdit(nota.contenuto || "");
    setModalita("visualizza");
    setErrore("");
  };

  const salva = async () => {
    setSalvataggio(true);
    setErrore("");
    try {
      const { data, error } = await supabase
        .from("guida")
        .update({
          titolo: titoloEdit || "Nuova nota",
          contenuto: contenutoEdit,
          updated_at: new Date().toISOString(),
        })
        .eq("id", nota.id)
        .select()
        .single();
      if (error) throw error;
      setModalita("visualizza");
      onSalva(data);
    } catch (e) {
      setErrore("Errore durante il salvataggio. Riprova.");
      console.error(e);
    } finally {
      setSalvataggio(false);
    }
  };

  const elimina = async () => {
    if (!window.confirm(`Eliminare la nota "${nota.titolo}"?`)) return;
    try {
      const { error } = await supabase.from("guida").delete().eq("id", nota.id);
      if (error) throw error;
      onElimina(nota.id);
    } catch (e) {
      setErrore("Errore durante l'eliminazione. Riprova.");
      console.error(e);
    }
  };

  return (
    <div style={S.bloccoNota}>
      {/* Header nota */}
      <div style={S.bloccoNotaHeader}>
        {modalita === "modifica" ? (
          <input
            style={S.titoloNotaEdit}
            value={titoloEdit}
            onChange={(e) => setTitoloEdit(e.target.value)}
            placeholder="Titolo nota..."
          />
        ) : (
          <span style={S.titoloNota}>✏️ {nota.titolo || "Nuova nota"}</span>
        )}

        <div style={S.azioniNota}>
          {modalita === "visualizza" ? (
            <>
              <button style={S.btnSm("ghost")} onClick={apriModifica}>Modifica</button>
              <button style={S.btnSm("danger")} onClick={elimina}>✕</button>
            </>
          ) : (
            <>
              <button
                style={{ ...S.btnSm("primary"), opacity: salvataggio ? 0.6 : 1 }}
                onClick={salva}
                disabled={salvataggio}
              >
                {salvataggio ? "..." : "Salva"}
              </button>
              <button style={S.btnSm("ghost")} onClick={annulla} disabled={salvataggio}>
                Annulla
              </button>
            </>
          )}
        </div>
      </div>

      {/* Errore */}
      {errore && (
        <div style={{ fontSize: 11, color: "#DC2626", marginBottom: 6 }}>{errore}</div>
      )}

      {/* Contenuto */}
      {modalita === "visualizza" ? (
        <div className="note-render">
          <NoteRenderer html={nota.contenuto} />
        </div>
      ) : (
        <RichEditor valore={contenutoEdit} onChange={setContenutoEdit} />
      )}
    </div>
  );
}

// ===== SINGOLO BLOCCO DESCRIZIONE PAGINA =====
// Riquadro grigio chiaro modificabile (admin-only in futuro multiutente; oggi single-user).
// Salva/legge da tabella `guida_descrizioni` (PK = sezione).
function BloccoDescrizione({ sezioneId, descrizione, onSalva }) {
  const [modalita, setModalita] = useState("visualizza"); // "visualizza" | "modifica"
  const [contenutoEdit, setContenutoEdit] = useState(descrizione?.contenuto || "");
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState("");

  // Aggiorna stato locale se la descrizione cambia dall'esterno (cambio sezione o refresh)
  useEffect(() => {
    setContenutoEdit(descrizione?.contenuto || "");
    setModalita("visualizza");
    setErrore("");
  }, [sezioneId, descrizione?.contenuto]);

  const apriModifica = () => {
    setContenutoEdit(descrizione?.contenuto || "");
    setModalita("modifica");
    setErrore("");
  };

  const annulla = () => {
    setContenutoEdit(descrizione?.contenuto || "");
    setModalita("visualizza");
    setErrore("");
  };

  const salva = async () => {
    setSalvataggio(true);
    setErrore("");
    try {
      const { data, error } = await supabase
        .from("guida_descrizioni")
        .update({
          contenuto: contenutoEdit,
          updated_at: new Date().toISOString(),
        })
        .eq("sezione", sezioneId)
        .select()
        .single();
      if (error) throw error;
      setModalita("visualizza");
      onSalva(data);
    } catch (e) {
      setErrore("Errore durante il salvataggio. Riprova.");
      console.error(e);
    } finally {
      setSalvataggio(false);
    }
  };

  // Pulsanti coerenti con BloccoNota ma in tonalità grigia (non gialla)
  const btnDesc = (variant) => ({
    fontSize: 10, fontWeight: 600, padding: "3px 8px",
    borderRadius: 5, cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    background: variant === "primary" ? "#475569" : "#F1F5F9",
    color: variant === "primary" ? "#fff" : "#475569",
    border: variant === "ghost" ? "1px solid #CBD5E1" : "none",
  });

  return (
    <div style={S.bloccoFisso}>
      {/* Header con pulsanti azione */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
        <div style={{ ...S.bloccoFissoLabel, marginBottom: 0 }}>📖 Descrizione pagina</div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {modalita === "visualizza" ? (
            <button style={btnDesc("ghost")} onClick={apriModifica}>Modifica</button>
          ) : (
            <>
              <button
                style={{ ...btnDesc("primary"), opacity: salvataggio ? 0.6 : 1 }}
                onClick={salva}
                disabled={salvataggio}
              >
                {salvataggio ? "..." : "Salva"}
              </button>
              <button style={btnDesc("ghost")} onClick={annulla} disabled={salvataggio}>
                Annulla
              </button>
            </>
          )}
        </div>
      </div>

      {/* Errore */}
      {errore && (
        <div style={{ fontSize: 11, color: "#DC2626", marginBottom: 6 }}>{errore}</div>
      )}

      {/* Contenuto */}
      {modalita === "visualizza" ? (
        <div
          style={{ fontSize: 13, color: "#374151", lineHeight: 1.75, whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: descrizione?.contenuto || "" }}
        />
      ) : (
        <RichEditor valore={contenutoEdit} onChange={setContenutoEdit} />
      )}
    </div>
  );
}

// ===== CONTENUTO SEZIONE NEL DRAWER =====
function SezioneDrawer({ sezioneId, sezioneLabel, sezioneIcon }) {
  const [descrizione, setDescrizione] = useState(null);
  const [caricamentoDescr, setCaricamentoDescr] = useState(true);
  const [note, setNote] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [aggiunta, setAggiunta] = useState(false);

  // Carica descrizione della sezione corrente
  useEffect(() => {
    const caricaDescrizione = async () => {
      setCaricamentoDescr(true);
      try {
        const { data, error } = await supabase
          .from("guida_descrizioni")
          .select("*")
          .eq("sezione", sezioneId)
          .single();
        if (error) throw error;
        setDescrizione(data);
      } catch (e) {
        console.error(e);
        setDescrizione(null);
      } finally {
        setCaricamentoDescr(false);
      }
    };
    caricaDescrizione();
  }, [sezioneId]);

  // Carica note della sezione corrente
  useEffect(() => {
    const carica = async () => {
      setCaricamento(true);
      try {
        const { data, error } = await supabase
          .from("guida")
          .select("*")
          .eq("sezione", sezioneId)
          .order("ordine", { ascending: true })
          .order("created_at", { ascending: true });
        if (error) throw error;
        setNote(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setCaricamento(false);
      }
    };
    carica();
  }, [sezioneId]);

  const aggiungiNota = async () => {
    setAggiunta(true);
    try {
      const ordine = note.length;
      const { data, error } = await supabase
        .from("guida")
        .insert({ sezione: sezioneId, titolo: "Nuova nota", contenuto: "", ordine })
        .select()
        .single();
      if (error) throw error;
      setNote((prev) => [...prev, data]);
    } catch (e) {
      console.error(e);
    } finally {
      setAggiunta(false);
    }
  };

  const onSalvaDescrizione = (descrizioneAggiornata) => {
    setDescrizione(descrizioneAggiornata);
  };

  const onSalva = (notaAggiornata) => {
    setNote((prev) => prev.map((n) => n.id === notaAggiornata.id ? notaAggiornata : n));
  };

  const onElimina = (id) => {
    setNote((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div>
      {/* Titolo sezione */}
      <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <span>{sezioneIcon}</span>
        <span>{sezioneLabel}</span>
      </div>

      {/* Blocco descrizione (modificabile) */}
      {caricamentoDescr ? (
        <div style={{ ...S.bloccoFisso, fontSize: 12, color: "#94A3B8" }}>
          Caricamento descrizione...
        </div>
      ) : (
        <BloccoDescrizione
          sezioneId={sezioneId}
          descrizione={descrizione}
          onSalva={onSalvaDescrizione}
        />
      )}

      {/* Blocchi note */}
      {caricamento ? (
        <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 12 }}>Caricamento note...</div>
      ) : (
        note.map((nota) => (
          <BloccoNota key={nota.id} nota={nota} onSalva={onSalva} onElimina={onElimina} />
        ))
      )}

      {/* Tasto aggiungi nota */}
      <button style={S.btnAggiungi} onClick={aggiungiNota} disabled={aggiunta}>
        {aggiunta ? "..." : "+ Aggiungi nota"}
      </button>
    </div>
  );
}

// ===== COMPONENTE PRINCIPALE DRAWER =====
export default function GuidaDrawer({ aperto, onChiudi, paginaAttiva }) {
  const [espanso, setEspanso] = useState(false);

  // Trova la sezione corrispondente alla pagina attiva
  const sezione = SEZIONI_GUIDA.find((s) => s.id === paginaAttiva) || SEZIONI_GUIDA[0];

  // Chiudi con tasto Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onChiudi(); };
    if (aperto) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [aperto, onChiudi]);

  // Resetta espanso quando si chiude
  useEffect(() => {
    if (!aperto) setEspanso(false);
  }, [aperto]);

  if (!aperto) return null;

  const larghezza = espanso ? "calc(100vw - 56px)" : 420;

  const btnIconStyle = {
    background: "none", border: "none", color: "#94A3B8",
    fontSize: 16, cursor: "pointer", padding: "4px 8px",
    borderRadius: 6, lineHeight: 1,
  };

  return (
    <>
      {/* Overlay — click fuori chiude (solo in modalità normale) */}
      {!espanso && <div style={S.overlay} onClick={onChiudi} />}

      {/* Drawer */}
      <div style={{ ...S.drawer, width: larghezza, transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)" }}>
        {/* Header */}
        <div style={S.drawerHeader}>
          <div style={S.drawerTitolo}>
            <span>📖</span>
            <span>Guida — {sezione.label}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Tasto espandi/riduci */}
            <button
              style={btnIconStyle}
              onClick={() => setEspanso(!espanso)}
              title={espanso ? "Riduci" : "Espandi"}
              onMouseEnter={(e) => e.currentTarget.style.color = "#fff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#94A3B8"}
            >
              {espanso ? "⊠" : "⛶"}
            </button>
            {/* Tasto chiudi */}
            <button
              style={btnIconStyle}
              onClick={onChiudi}
              title="Chiudi guida"
              onMouseEnter={(e) => e.currentTarget.style.color = "#fff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#94A3B8"}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body scrollabile */}
        <div style={S.drawerBody}>
          <SezioneDrawer
            key={sezione.id}
            sezioneId={sezione.id}
            sezioneLabel={sezione.label}
            sezioneIcon={sezione.icon}
          />
        </div>
      </div>
    </>
  );
}
